import { Form, App } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import CustomInput from "../../../../components/CustomInput/Input";
import CustomSelect from "../../../../components/CustomSelect/CustomSelect";
import {
  nameRules,
  emailRules,
  phoneNumberRules,
  capitalizeWords,
} from "../../../../helpers/helpers";
import { getAllRoles } from "../../../../redux/slices/RoleSlice";
import {
  getUserList,
  getOrgList,
  addUserRoles,
  updateUser,
  getAllCities,
  getRegionalManagers,
} from "../../../../redux/slices/UserSlice";
import {
  BTN_TXT,
  ERROR_MSGS,
  ASSIGNRMFOR,
} from "../../../../constants/constants";
import FormFooter from "../../../../components/CustomForms/FormFooter";
import ErrorAlert from "../../../../components/ErrorAlert/ErrorAlert";
import { logoutUser } from "../../../../redux/slices/AuthSlice";

const UserForm = ({ isEditMode, editingUser, onClose }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { addUserLoading, error, orgList, regionalManagers } = useSelector(
    (state) => state.user,
  );
  const { user } = useSelector((state) => state.auth);
  const { roleList } = useSelector((state) => state.role);
  const watchedRole = Form.useWatch("role", form);

  useEffect(() => {
    dispatch(getOrgList());
    dispatch(getAllRoles(true));
    dispatch(getRegionalManagers());
    dispatch(getAllCities());
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && editingUser) {
      form.setFieldsValue({
        userName: editingUser.userName,
        email: editingUser.email,
        mobile: editingUser.mobile,
        busName: editingUser.orgId,
        role: editingUser.roleId,
        regionalManager: editingUser.parentUserId,
        assignFor: editingUser.roleForBusiness
          ? "business"
          : editingUser.roleForInvestor
            ? "investor"
            : undefined,
      });
    } else {
      form.resetFields();
    }
  }, [isEditMode, editingUser, form]);

  // clear dependent fields when the specific role is not selected
  useEffect(() => {
    if (!watchedRole) return;
    if (watchedRole !== "ROL1000005") {
      form.setFieldValue("assignFor", undefined);
    }

    if (watchedRole !== "ROL1000005") {
      form.setFieldValue("regionalManager", undefined);
    }
  }, [watchedRole, form]);

  const handleSubmitUser = async (values) => {
    const payload = {
      name: values.userName,
      email: values.email,
      mobile: values.mobile,
      orgId: values.busName,
      roleId: values.role,
      userId: isEditMode && editingUser ? editingUser.userId : undefined,
      parentUserId: values.regionalManager,
      forInvestor: values.assignFor === "investor",
      forBusiness: values.assignFor === "business",
    };
    const response = await dispatch(
      isEditMode ? updateUser(payload) : addUserRoles(payload),
    );
    if (
      response.payload.statusCode === 201 ||
      response.payload.statusCode === 200
    ) {
      onClose();
      if (
        isEditMode &&
        user.email !== values.email &&
        user.userId === editingUser.userId
      ) {
        const res = await dispatch(logoutUser());
        if (res.payload.statusCode === 201) {
          localStorage.clear();
          window.location.href = "/";
          message.success(
            "User updated successfully. Please login again with new credentials.",
          );
        }
        return;
      }
      form.resetFields();
      dispatch(getUserList());
    }
  };

  return (
    <Form layout="vertical" form={form} onFinish={handleSubmitUser}>
      <CustomInput
        name="userName"
        label="User Name"
        placeholder="Enter User Name"
        isRequired={true}
        rules={nameRules("User")}
      />

      <CustomSelect
        name="busName"
        label="Organization"
        placeholder="Select Organization"
        disabled={isEditMode} // disable in edit mode
        isRequired={true}
        rules={[{ required: true, message: ERROR_MSGS.SEL_ORG_ERR_MSG }]}
        options={orgList?.map((org) => ({
          value: org.orgId,
          label: capitalizeWords(org.busName),
        }))}
      />

      <CustomInput
        name="email"
        label="Email"
        placeholder="Enter Email"
        isRequired={true}
        rules={emailRules("")}
      />

      <CustomInput
        name="mobile"
        label="Mobile Number"
        placeholder="Enter Mobile Number"
        prefix="+91"
        isRequired={true}
        rules={phoneNumberRules("")}
      />

      <CustomSelect
        name="role"
        label="Roles"
        placeholder="Select Role"
        isRequired={true}
        rules={[{ required: true, message: ERROR_MSGS.SEL_ROLE_ERR_MSG }]}
        options={roleList?.map((role) => ({
          value: role.roleId,
          label: capitalizeWords(role.roleName),
        }))}
      />

      {watchedRole === "ROL1000005" && (
        <CustomSelect
          name="assignFor"
          label="Assign RM For"
          placeholder="Select Assign RM For"
          isRequired={true}
          rules={[
            { required: true, message: ERROR_MSGS.SEL_ASS_RM_FOR_ERR_MSG },
          ]}
          options={ASSIGNRMFOR?.map((type) => ({
            value: type.value,
            label: capitalizeWords(type.label),
          }))}
        />
      )}

      {watchedRole === "ROL1000005" && (
        <CustomSelect
          name="regionalManager"
          label="Select Regional Manager"
          placeholder="Select Regional Manager"
          isRequired={true}
          rules={[{ required: true, message: ERROR_MSGS.SEL_RM_MAN_ERR_MSG }]}
          options={regionalManagers?.map((type) => ({
            value: type.userId,
            label: capitalizeWords(type.userName),
          }))}
        />
      )}

      {error && <ErrorAlert error={error} />}

      <FormFooter
        onCancelClick={() => {
          onClose();
          form.resetFields();
        }}
        submitLoading={addUserLoading}
        btnName={isEditMode ? BTN_TXT.SAVE : BTN_TXT.CREATE}
      />
    </Form>
  );
};

export default UserForm;
